import { ATLAS_AI_NOTE_WORKING } from '@common/constants/webSockets'
import { IBBCFlagDetailsData, IFlagData, IFlags, IFlagsOptions } from '@common/interfaces/bbc'
import orderBy from 'lodash/orderBy'
import moment from 'moment'
import { combineReducers } from 'redux'
import { createLoadingDataReducer } from '../../helpers/redux'
import { IAction, IActionWithParams, IPaginationParams } from '../apiMiddleware'
import { addNoteSuccess, deleteNoteSuccess, updateNoteSuccess } from '../common/notes'
import { ADD_NOTE_SUCCESS, DELETE_NOTE_SUCCESS, UPDATE_NOTE_SUCCESS } from '../notes/actions'
import * as actions from './actions'

interface IFlagSummaryMetrics {
  redFlagsCount: number
  yellowFlagsCount: number
  impactToAvailability: number
  distinctClientsCount: number
}

export default combineReducers({
  flags(state: IFlagData = null, action: IActionWithParams<IPaginationParams>): IFlagData {
    switch (action.type) {
      case actions.LIST_SUCCESS: {
        const data = action.data as IFlagData
        if (!action.params?.loadMore) {
          return data
        }
        return {
          ...state,
          data: [...state.data, ...data.data],
        }
      }
      case actions.UPDATE_FLAG_SUCCESS: {
        const updatedFlag = action.data as IFlags
        const isResolvedFirst = state?.data?.[0]?.resolved
        const resolvedOrder = isResolvedFirst ? 'desc' : 'asc'
        const firstRecord = state?.data?.[0]
        const lastRecord = state?.data?.[state.data.length - 1]
        const recordDateOrder = moment(firstRecord?.recordDate).isBefore(lastRecord?.recordDate)
          ? 'asc'
          : 'desc'

        return {
          ...state,
          data: orderBy(
            state.data.map((item) => {
              if (item.id === updatedFlag.id) {
                return updatedFlag
              }
              return item
            }),
            ['resolved', 'recordDate'],
            [resolvedOrder, recordDateOrder],
          ),
        }
      }
      case ADD_NOTE_SUCCESS:
        return addNoteSuccess(state, action)
      case DELETE_NOTE_SUCCESS:
        return deleteNoteSuccess(state, action)
      case UPDATE_NOTE_SUCCESS:
        return updateNoteSuccess(state, action)
      // Arc flipped its working state on a flag's comment thread (pushed live
      // over the websocket). Update the matching flag's root note
      // `isArcWorking` so the "Arc is working…" indicator appears/disappears
      // without a hard refresh — mirrors the notes reducer, since a flag's
      // `isArcWorking` lives on its `notes` here, not in the notes store.
      case ATLAS_AI_NOTE_WORKING: {
        if (!state?.data) {
          return state
        }
        const { noteId, isArcWorking } = action.data as {
          noteId: string
          isArcWorking: boolean
        }
        return {
          ...state,
          data: state.data.map((item) =>
            item.notes && item.notes.id === noteId
              ? { ...item, notes: { ...item.notes, isArcWorking } }
              : item,
          ),
        }
      }
      default:
        return state
    }
  },
  isLoading(state = false, action: IActionWithParams<IPaginationParams>) {
    switch (action.type) {
      case actions.LIST_REQUEST:
        // `skipLoader` (used by the Arc-complete refetch) reloads the list in
        // the background without flashing the skeleton loaders.
        if (action.params?.loadMore || (action.params as { skipLoader?: boolean })?.skipLoader) {
          return false
        }
        return true
      case actions.LIST_SUCCESS:
      case actions.LIST_FAILURE:
        return false
      default:
        return state
    }
  },
  // Records the params of the most recent (non-loadMore) flags fetch so the
  // Arc-complete refetch saga can silently reload the exact same list — the
  // component-held filter/pagination params aren't otherwise in redux.
  lastListContext(
    state: object | null = null,
    action: IActionWithParams<IPaginationParams>,
  ): object | null {
    switch (action.type) {
      case actions.LIST_REQUEST:
        if (action.params?.loadMore) {
          return state
        }
        return action.params ?? {}
      default:
        return state
    }
  },
  flagDetails: createLoadingDataReducer<IBBCFlagDetailsData>([
    actions.GET_FLAG_DETAILS_PAID_PRIORITY_INVOICES_REQUEST,
    actions.GET_FLAG_DETAILS_PAID_INVOICE_REQUEST,
    actions.GET_FLAG_DETAILS_DUPLICATE_INVOICES_REQUEST,
    actions.GET_FLAG_DETAILS_REQUEST,
  ]),
  newFlagsCount(state = 0, action: IAction) {
    switch (action.type) {
      case actions.GET_NEW_FLAGS_COUNT_SUCCESS: {
        const data = action.data as { count: number } | undefined
        return data?.count ?? state
      }
      default:
        return state
    }
  },
  flagOptions: createLoadingDataReducer<IFlagsOptions>([actions.GET_FLAGS_OPTIONS_REQUEST]),
  summaryMetrics: createLoadingDataReducer<IFlagSummaryMetrics>([
    actions.GET_FLAGS_SUMMARY_METRICS_REQUEST,
  ]),
})
